// Copyright © 2017-2019  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC

<template>
  <v-container fluid grid-list-md style="margin-bottom:100px;">
    <v-row wrap>
      <v-col cols="12" justify-center>
        <v-card>
          <v-data-table
            mobile-breakpoint="0"
            :headers="tableHeaders"
            :items="items"
            :loading="loading"
            :no-data-text="noDataText"
            :items-per-page="25"
            :footer-props="{
              'items-per-page-options': [10, 25, 100]
            }"
          >
            <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
            <template slot="item" slot-scope="props">
              <tr
                :style="rowStyle"
                @click="editItem(getEditId(props.item))"
              >
                <td class="text-xs-left">{{ props.item.name }}</td>
                <td class="text-xs-left">{{ props.item.description }}</td>
                <td class="text-xs-left">{{ formatCategories(props.item, categoriesData) }}</td>
                <td class="text-xs-left">{{ formatTags(props.item) }}</td>
                <td style="width: 1%; padding: 0px; white-space: nowrap;" @click.stop>
                  <span style="display: inline-flex;">
                    <v-btn
                      v-if="props.item.uuid"
                      title="Copy Share URL"
                      icon
                      text
                      @click="copyShareUrl(props.item)"
                    >
                      <v-icon>content_copy</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="canDelete"
                      title="Delete"
                      icon
                      text
                      @click="deleteDialog(props.item)"
                    >
                      <v-icon>delete</v-icon>
                    </v-btn>
                  </span>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>

        <v-dialog v-model="showDeleteDialog" max-width="300px">
          <v-card>
            <v-card-title>
              <div class="headline">Delete?</div>
            </v-card-title>
            <v-card-text>Are you sure you want to delete this share?  This action cannot be undone.</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click.native="showDeleteDialog = false">Cancel</v-btn>
              <v-btn text color="red" @click.native="deleteItem">I'm Sure</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-btn
          v-if="canCreate"
          dark
          color="green"
          class="mt-4 py-6 px-6 rounded-lg"
          @click="editItem('new')"
          id="addButton"
        >
          New Collection
          <v-icon right>add</v-icon>
        </v-btn>
        <error-snackbar v-model="errorText"></error-snackbar>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { mapGetters } from 'vuex'
import ErrorSnackbar from '@sdgsystems/vue-common/components/ErrorSnackbar'
import { AXIOSAPI } from '@sdgsystems/vue-common/components/requests-common'
import { checkPermission } from '@sdgsystems/vue-common/components/user'
import { PERMISSIONS } from '@sdgsystems/vue-common/components/constants'
import { ENDPOINT } from '@/shared/constants'

export default {
  components: {
    ErrorSnackbar
  },

  props: {
    searchBarText: String
  },

  data () {
    return {
      endpoint: ENDPOINT,
      items: [],
      loading: true,
      categoriesData: [],
      showDeleteDialog: false,
      deleteQueue: null,
      errorText: '',
      noDataText: 'No data available'
    }
  },

  computed: {
    ...mapGetters([
      'accessRules'
    ]),

    canCreate: function () {
      return checkPermission(this.accessRules, 'users', PERMISSIONS.CREATE)
    },

    canDelete: function () {
      return checkPermission(this.accessRules, 'users', PERMISSIONS.DELETE)
    },

    tableHeaders: function () {
      return [
        { text: 'Name', value: 'name', sortable: true, align: 'left' },
        { text: 'Description', value: 'description', sortable: false, align: 'left' },
        { text: 'Categories', value: 'categories', sortable: false, align: 'left' },
        { text: 'Tags', value: 'tags', sortable: false, align: 'left' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'left' }
      ]
    },

    rowStyle: function () {
      return 'cursor:pointer;'
    }
  },

  created () {
    this.loadContext().then(() => {
      this.getData()
    })
  },

  methods: {
    getEditId: function (item) {
      return item.id || ''
    },

    editItem: function (id) {
      if (id === '') {
        id = 'new'
      }
      this.$router.push({ name: 'shareEdit', params: { id: id } })
    },

    formatCategories: function (item, categoriesData) {
      if (!item.categoryIDs || !Array.isArray(item.categoryIDs)) return ''
      if (!categoriesData || !Array.isArray(categoriesData)) return item.categoryIDs.join(', ')
      return item.categoryIDs.map(function (id) {
        var cat = categoriesData.find(function (c) {
          return c.id === id || String(c.id) === String(id)
        })
        return cat ? cat.name : id
      }).join(', ')
    },

    formatTags: function (item) {
      if (!item.tags || !Array.isArray(item.tags)) return ''
      return item.tags.map(function (t) {
        return typeof t === 'string' ? t : (t.name || t.id || '')
      }).join(', ')
    },

    loadContext: function () {
      return AXIOSAPI.get(this.endpoint + 'image-categories')
        .then(response => {
          this.categoriesData = response.data
        })
        .catch(e => {
          this.errorText = 'Failed to load categories: ' + e
        })
    },

    getData: function () {
      this.loading = true
      AXIOSAPI.get(this.endpoint + 'shares')
        .then(response => {
          this.items = response.data
          this.loading = false
        })
        .catch(e => {
          this.errorText = 'Failed to load Shares: ' + e
          this.loading = false
        })
    },

    copyShareUrl: function (item) {
      var shareUrl = window.location.origin + '/#/share/' + item.uuid
      navigator.clipboard.writeText(shareUrl).then(() => {
        this.errorText = 'Share URL copied to clipboard'
      }).catch(e => {
        console.error('Failed to copy share URL: ' + e)
        this.errorText = 'Failed to copy share URL: ' + e
      })
    },

    deleteDialog: function (row) {
      this.showDeleteDialog = true
      this.deleteQueue = row
    },

    deleteItem: function () {
      this.showDeleteDialog = false
      var id = this.deleteQueue.id
      AXIOSAPI.delete(this.endpoint + 'shares/' + id)
        .then(() => {
          this.getData()
        })
        .catch(e => {
          this.errorText = 'Delete failed: ' + (e.response && e.response.data ? e.response.data : e)
        })
      this.deleteQueue = null
    }
  }
}
</script>

<style scoped>
  #addButton {
    position: relative;
    overflow: hidden;
    position: fixed;
    bottom: 20px !important;
    right: 20px;
  }
</style>



// WEBPACK FOOTER //
// Share.vue?72896a13